import { useMemo, type ReactNode } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerItemData,
    PickerSectionData,
    PickerTriggerElement,
} from "@design-system/Picker/Picker.types";
import { PlusIcon } from "@icons";
import type { AnatomyForcedState } from "./componentAnatomyMode";
import {
    buildDataSections,
    buildFlatItems,
    type PickerPlaygroundButtonVariant,
    type PickerPlaygroundFeatures,
    type PickerPlaygroundTrigger,
} from "./PickerPlayground.lib";
import { buildTreeItems, buildTreeSections } from "./PickerPlaygroundTree";
import { PickerCellTableHost } from "./PickerPlaygroundViewTable";
import type { PickerPlaygroundPaging } from "./usePickerPlaygroundPagedItems";

export type StageValue = string[] | string | null;

export interface StageProps {
    features: PickerPlaygroundFeatures;
    forcedState?: AnatomyForcedState;
    onValueChange: (value: StageValue) => void;
    onFooterSelect: (query: string) => void;
    onPagingChange: (paging: PickerPlaygroundPaging) => void;
}

const isCellPreset = (trigger: string | undefined) =>
    trigger === "cell" || trigger === "cellInput";

/** A cell preset lives in a real one-row `ViewTable`; other triggers stand alone. */
export const StageHost = ({
    trigger,
    children,
}: {
    trigger: PickerPlaygroundTrigger;
    children: ReactNode;
}) =>
    isCellPreset(trigger) ? (
        <PickerCellTableHost
            rows={[{ id: "stage", label: "Stage row", cell: children }]}
            cellHeader={trigger}
        />
    ) : (
        children
    );

/**
 * The consumer `Picker.Button` the `button` stage hands to `Picker.Select`;
 * `primary` omits the trigger so the stage exercises the root's default.
 * `Picker.Button` is the childless trigger element (same props as `Button`);
 * any `Button` with its own children still works as a trigger.
 */
export const buildStageButton = (
    variant: PickerPlaygroundButtonVariant,
): PickerTriggerElement | undefined => {
    switch (variant) {
        case "primary":
            return undefined;
        case "link":
            return <Picker.Button variant="link" size="sm" />;
        case "ghost":
            return <Picker.Button variant="ghost" leftIcon={<PlusIcon />} />;
        case "accent":
            return <Picker.Button variant="accent" leftIcon={<PlusIcon />} />;
        case "secondary":
            return (
                <Picker.Button
                    variant="secondary"
                    size="md"
                    leftIcon={<PlusIcon />}
                />
            );
        default:
            return (
                <Picker.Button
                    variant={variant}
                    size="md"
                    leftIcon={<PlusIcon />}
                />
            );
    }
};

export const renderStageFooter =
    (label: string, onFooterSelect: (query: string) => void) =>
    (query: string) => (
        <Picker.Item
            leading={<PlusIcon />}
            onSelect={() => onFooterSelect(query)}
        >
            {query.trim() === "" ? label : `Add "${query.trim()}"`}
        </Picker.Item>
    );

/** Data the toggles ask for: flat, tree, sectioned (flat or tree) or empty. */
export const useStageData = (
    features: PickerPlaygroundFeatures,
    onFooterSelect: (query: string) => void,
) => {
    const flatItems = useMemo(() => buildFlatItems(features), [features]);
    const dataSections = useMemo(() => buildDataSections(features), [features]);
    const treeDecoration = useMemo(
        () => ({
            ...features,
            renderFlyoutFooter: renderStageFooter(
                "Add account",
                onFooterSelect,
            ),
        }),
        [features, onFooterSelect],
    );
    const treeItems = useMemo(
        () => buildTreeItems(treeDecoration),
        [treeDecoration],
    );
    const treeSections = useMemo(
        () => buildTreeSections(treeDecoration),
        [treeDecoration],
    );

    let items: Array<PickerItemData<string>> | undefined = flatItems;
    let sections: Array<PickerSectionData<string>> | undefined;
    if (features.isEmpty) {
        items = [];
    } else if (features.hasSections) {
        items = undefined;
        sections = features.isTree ? treeSections : dataSections;
    } else if (features.isTree) {
        items = treeItems;
    }
    // `all` uses the root default so the sandbox exercises `flyoutDefaults`;
    // `first` / `deepest` are per-item overrides on the sample data.
    const flyoutDefaults = {
        searchable: features.flyoutSearch === "all" ? true : undefined,
        renderFooter:
            features.flyoutFooter === "all"
                ? renderStageFooter("Add account", onFooterSelect)
                : undefined,
    };
    return { items, sections, flyoutDefaults };
};
